import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IRateioResponse, RateioModeEnum } from 'Data/interfaces/Faturamento/IRateio';
import { ISpeResponse } from 'Data/interfaces/Faturamento/ISpe';
import { numberToMoney } from 'Utils/Money';
import {
  Amount,
  Box,
  ConfigLink,
  Document,
  Empty,
  Footer,
  Header,
  List,
  Name,
  Row,
  Segment,
  Status,
  Title,
  Total,
  Track,
  Weight,
} from './styles';

interface IRateioSummary {
  rateio: IRateioResponse;
  spes: ISpeResponse[];
  planPrice?: number;
  onConfigure?: () => void;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const RateioSummary: FC<IRateioSummary> = ({
  rateio,
  spes,
  planPrice,
  onConfigure,
}) => {
  const { t } = useTranslation('faturamento');

  const rows = useMemo(() => {
    return rateio.Shares.map((share) => {
      const spe = spes.find((item) => item.SpeId === share.SpeId);
      return {
        speId: share.SpeId,
        name: spe?.CompanyName ?? '',
        document: spe?.CNPJ ?? '',
        weight: share.Weight,
        amount: planPrice ? round2((planPrice * share.Weight) / 100) : undefined,
      };
    });
  }, [rateio.Shares, spes, planPrice]);

  const total = useMemo(() => {
    return round2(rateio.Shares.reduce((sum, share) => sum + share.Weight, 0));
  }, [rateio.Shares]);

  const isComplete = total === 100;

  // No estouro, o excedente vira um segmento próprio em vermelho: o erro fica
  // visível na própria barra, antes de o número ser lido.
  const segments = useMemo(() => {
    const result: { key: string; width: number; over?: boolean }[] = [];
    let used = 0;

    rateio.Shares.forEach((share) => {
      const fits = Math.max(0, Math.min(share.Weight, 100 - used));
      if (fits > 0) {
        result.push({ key: share.SpeId, width: fits });
      }
      const excess = share.Weight - fits;
      if (excess > 0) {
        result.push({ key: `${share.SpeId}-over`, width: excess, over: true });
      }
      used += share.Weight;
    });

    return result;
  }, [rateio.Shares]);

  const modeLabel =
    rateio.Mode === RateioModeEnum.Uniform
      ? t('rateio.modeUniform')
      : t('rateio.modeCustom');

  if (spes.length === 0) {
    return (
      <Box>
        <Header>
          <Title>{t('rateio.summaryTitleEmpty')}</Title>
          {onConfigure ? (
            <ConfigLink type="button" onClick={onConfigure}>
              {t('rateio.configure')}
            </ConfigLink>
          ) : null}
        </Header>
        <Empty>{t('rateio.summaryEmptyDescription')}</Empty>
      </Box>
    );
  }

  return (
    <Box>
      <Header>
        <Title>
          {t('rateio.summaryTitle')}{' '}
          <em>
            · {t('rateio.speCount', { count: spes.length })} · {modeLabel}
          </em>
        </Title>
        {onConfigure ? (
          <ConfigLink type="button" onClick={onConfigure}>
            {t('rateio.configure')}
          </ConfigLink>
        ) : null}
      </Header>

      <Track>
        {segments.map((segment) => (
          <Segment key={segment.key} $width={segment.width} $over={segment.over} />
        ))}
      </Track>

      <List>
        {rows.map((row) => (
          <Row key={row.speId}>
            <Name>{row.name}</Name>
            <Document>{row.document}</Document>
            <Weight>{`${row.weight.toFixed(2).replace('.', ',')}%`}</Weight>
            {row.amount !== undefined ? (
              <Amount>{numberToMoney(row.amount, 'real')}</Amount>
            ) : null}
          </Row>
        ))}
      </List>

      <Footer>
        <Status $ok={isComplete}>
          {isComplete
            ? t('rateio.totalDistributed')
            : t('rateio.totalIncomplete', {
                total: total.toFixed(2).replace('.', ','),
              })}
        </Status>
        <Total>
          {`${total.toFixed(2).replace('.', ',')}%`}
          {planPrice ? ` · ${numberToMoney(round2((planPrice * total) / 100), 'real')}` : ''}
        </Total>
      </Footer>
    </Box>
  );
};

export default RateioSummary;
